import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const SCORES = {
  a:1, b:3, c:3, d:2, e:1, f:4, g:2, h:4, i:1, j:8, k:5,
  l:1, m:3, n:1, o:1, p:3, q:10, r:1, s:1, t:1, u:1,
  v:4, w:4, x:8, y:4, z:10
};
const TILE_COLORS = {
  1:'#e5e7eb', 2:'#bfdbfe', 3:'#bbf7d0', 4:'#fde68a', 5:'#fca5a5',
  8:'#c4b5fd', 10:'#f9a8d4'
};

const MULTIPLIERS = [
  { label:'None', dw:1, dl:1, tw:1, tl:1 },
  { label:'DW ×2', dw:2, dl:1, tw:1, tl:1 },
  { label:'TW ×3', dw:1, dl:1, tw:3, tl:1 },
  { label:'DL ×2', dw:1, dl:2, tw:1, tl:1 },
  { label:'TL ×3', dw:1, dl:1, tw:1, tl:3 },
];

export default function ScrabbleCalculator() {
  const [word, setWord]           = useState('');
  const [multi, setMulti]         = useState(0);
  const [tileMulti, setTileMulti] = useState({});

  const letters = word.toLowerCase().replace(/[^a-z]/g, '').split('');
  const selected = MULTIPLIERS[multi];

  function tileScore(i) {
    const base = SCORES[letters[i]] || 0;
    const tm = tileMulti[i] || 1;
    return base * tm;
  }

  const letterTotal = letters.reduce((s, _, i) => s + tileScore(i), 0);
  const total = letterTotal * (selected.dw * selected.tw);

  function toggleTileMulti(i) {
    setTileMulti(prev => {
      const cur = prev[i] || 1;
      return { ...prev, [i]: cur === 1 ? 2 : cur === 2 ? 3 : 1 };
    });
  }

  return (
    <div className="game-page">
      <PageSEO page="scrabble-calculator" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🧮</span>
          <h1>Scrabble Score Calculator</h1>
          <p>Calculate the exact score for any Scrabble word with bonus square multipliers!</p>
        </div>

        <div className="solver-card">
          <div className="mb-3">
            <label className="fw-bold mb-1">Enter Word</label>
            <input
              className="big-input"
              value={word}
              onChange={e => { setWord(e.target.value.replace(/[^a-zA-Z]/g, '')); setTileMulti({}); }}
              placeholder="e.g. QUARTZ"
              maxLength={15}
              autoFocus
            />
          </div>

          <div className="mb-3">
            <label className="fw-bold mb-1">Word Multiplier Square</label>
            <div className="d-flex flex-wrap gap-2">
              {MULTIPLIERS.map((m, i) => (
                <button key={m.label} type="button"
                  className="btn btn-sm fw-bold"
                  onClick={() => setMulti(i)}
                  style={{
                    borderRadius:50, border:'2px solid', fontFamily:'Nunito,sans-serif',
                    background: multi === i ? 'var(--primary)' : 'transparent',
                    borderColor: multi === i ? 'var(--primary)' : 'var(--border)',
                    color: multi === i ? '#fff' : 'var(--text)',
                  }}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {letters.length > 0 && (
          <div className="solver-card">
            <p className="fw-bold text-muted mb-1 small">Click a tile to add letter multiplier (×2 DL / ×3 TL)</p>
            <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center">
              {letters.map((ch, i) => {
                const score = SCORES[ch] || 0;
                const tm = tileMulti[i] || 1;
                return (
                  <div key={i} onClick={() => toggleTileMulti(i)}
                    style={{
                      cursor:'pointer', userSelect:'none',
                      width:56, height:64, borderRadius:8, position:'relative',
                      background: TILE_COLORS[score] || '#e5e7eb',
                      border: tm > 1 ? `3px solid var(--primary)` : '2px solid #d1d5db',
                      display:'flex', flexDirection:'column',
                      alignItems:'center', justifyContent:'center',
                      boxShadow:'0 2px 6px rgba(0,0,0,0.1)',
                      transition:'transform 0.1s',
                    }}>
                    <span style={{fontSize:'1.5rem', fontWeight:900, fontFamily:'Fredoka One, cursive', lineHeight:1}}>
                      {ch.toUpperCase()}
                    </span>
                    <span style={{fontSize:'0.65rem', fontWeight:700, color:'#6b7280'}}>
                      {score}{tm > 1 ? `×${tm}` : ''}
                    </span>
                    {tm > 1 && (
                      <span style={{
                        position:'absolute', top:-8, right:-8,
                        background:'var(--primary)', color:'#fff',
                        borderRadius:'50%', width:18, height:18,
                        fontSize:'0.65rem', fontWeight:900,
                        display:'flex', alignItems:'center', justifyContent:'center',
                      }}>×{tm}</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="text-center p-4 rounded-3" style={{background:'linear-gradient(135deg,#7c3aed22,#4f46e522)'}}>
              <div className="text-muted fw-bold small mb-1">TOTAL SCORE</div>
              <div style={{fontSize:'4rem', fontWeight:900, color:'var(--primary)', fontFamily:'Fredoka One, cursive', lineHeight:1}}>
                {total}
              </div>
              {selected.dw > 1 && <div className="text-muted small mt-1">Double Word Score applied (×{selected.dw})</div>}
              {selected.tw > 1 && <div className="text-muted small mt-1">Triple Word Score applied (×{selected.tw})</div>}
              {selected.dl > 1 && <div className="text-muted small mt-1">Double Letter Score applied (×{selected.dl})</div>}
              {selected.tl > 1 && <div className="text-muted small mt-1">Triple Letter Score applied (×{selected.tl})</div>}
              <div className="text-muted small mt-2">Letter total: {letterTotal} · Word multiplier: ×{selected.dw * selected.tw}</div>
            </div>

            <div className="mt-3 p-3 rounded-3" style={{background:'var(--surface)'}}>
              <div className="fw-bold text-muted small mb-2">SCRABBLE TILE VALUES</div>
              <div className="d-flex flex-wrap gap-1">
                {Object.entries(SCORES).sort((a,b) => a[1]-b[1]).map(([ch, sc]) => (
                  <span key={ch} className="badge" style={{
                    background: TILE_COLORS[sc] || '#e5e7eb',
                    color:'#374151', fontSize:'0.75rem', fontWeight:700,
                  }}>
                    {ch.toUpperCase()}={sc}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        <SeoContent game="scrabble-calculator" />
      </div>
    </div>
  );
}
